import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {ApiClientService} from '../../../services/api-client.service';
import {ParcelsService} from '../../../services/parcels.service';
import {Parcel, ResidentForSelect, SettingsSms, SuiteUser, UserPhoneType} from '../../../types/models';
import {UrlService} from '../../../services/url.service';
import {ConstantsService} from '../../../services/constants.service';
import {AccountPermissionsService} from '../../../services/account-permissions.service';
import {BuildingApiService} from '../../../services/api/building-api.service';
import {finalize, flatMap, map, tap} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';
import {untilDestroyed} from 'ngx-take-until-destroy';
import {SmsNotificationApiService} from '../../../services/api/sms-notification-api.service';
import {IBuilding} from '../../../types/api-response/building';

declare const toastr: any;
declare const $: any;
declare const swal: any;

@Component({
    selector: 'parcel-modal',
    templateUrl: './parcel-modal.component.html',
    styleUrls: ['./parcel-modal.component.scss']
})
export class ParcelModalComponent implements OnInit, OnDestroy {
    showModal = false;
    suites: Array<any> = [];
    residents: Array<ResidentForSelect> = [];
    useParcelsWaiver = false;
    smsSettings = null;
    selectedResident: ResidentForSelect;
    selectedSuiteUser: SuiteUser;
    parcelTypes: any[] = [];
    buildingId = new FormControl(null, Validators.required);
    suiteId = new FormControl(null, Validators.required);
    residentId = new FormControl(null, Validators.required);
    parcelTypeId = new FormControl(null);
    parcelPostServiceId = new FormControl();
    numberPieces = new FormControl(null, Validators.min(1));
    form: FormGroup;
    data: Parcel;
    isEditModal: boolean;
    loading = {
        suites: false,
        residents: false,
        global: false
    };

    private _selectedBuildingCorporation = null;

    static parseTime(dateString: string): string {
        return (new DatePipe('en-US')).transform(dateString, 'hh:mm a');
    }

    constructor(
        public api: ApiClientService,
        public parcelsService: ParcelsService,
        public urlService: UrlService,
        public accountService: AccountPermissionsService,
        private buildingApiService: BuildingApiService,
        private smsNotificationApiService: SmsNotificationApiService,
        private accountPermissionsService: AccountPermissionsService
    ) {
        this.form = new FormGroup({
            inOut: new FormControl('Incoming'),
            buildingId: this.buildingId,
            suiteId: this.suiteId,
            suiteNumber: new FormControl(null, Validators.compose([
                Validators.required,
                Validators.maxLength(255)
            ])),
            residentId: this.residentId,
            residentFullName: new FormControl(null, Validators.compose([
                Validators.required,
                Validators.maxLength(255)
            ])),
            parcelTypeId: this.parcelTypeId,
            numberPieces: this.numberPieces,
            parcelPostServiceId: this.parcelPostServiceId,
            parcelPostServiceName: new FormControl('', Validators.maxLength(255)),
            barCode: new FormControl('', Validators.maxLength(255)),
            date: new FormControl((new Date).toISOString(), Validators.compose([Validators.required])),
            time: new FormControl(ParcelModalComponent.parseTime((new Date()).toISOString())),
            description: new FormControl('', Validators.maxLength(255)),
            emailNotice: new FormControl(true),
            smsNotice: new FormControl(true)
        });
        this.listenToValueChanges();
    }

    get selectedBuildingCorporation(): any {
        return this._selectedBuildingCorporation;
    }

    set selectedBuildingCorporation(value: any) {
        this._selectedBuildingCorporation = value;
        if (value) {
            this.parcelTypes = this.parcelsService.parcelTypes.filter(
                t => t.parcelServiceSettings == this._selectedBuildingCorporation
            );
        } else {
            this.parcelTypes = [];
        }
    }

    ngOnInit() {
        this.initData();
        this.initTimePicker();
        this.urlService.modalOpened.emit();
    }

    initTimePicker() {
        $('#timepicker1').timepicker().on('changeTime.timepicker', e => this.form.patchValue({time: e.time.value}));
    }

    openTimepicker(): void {
        $('#timepicker1').timepicker('showWidget');
    }

    initData() {
        if (this.parcelsService.parcelInParcelModal) {
            this.data = this.parcelsService.parcelInParcelModal;
            this.isEditModal = !!this.data.id;
            const {createdAt, buildingId, suiteId, residentId} = this.parcelsService.parcelInParcelModal;
            this.form.patchValue({
                ...this.parcelsService.parcelInParcelModal,
                residentId: residentId ? residentId : 0,
                suiteId: suiteId ? suiteId : 0,
                date: createdAt,
                time: ParcelModalComponent.parseTime(createdAt)
            }, {emitEvent: false});
            this.getSuites(buildingId, suiteId)
                .pipe(
                    tap(
                        () => {
                            if (this.residentId.value) {
                                this.changeResident(this.residentId.value);
                            }
                        }
                    )
                ).subscribe();
        } else {
            const b = this.urlService.queryParams['building'];
            if (b) {
                this.changeBuilding(b);
            }

            if (this.parcelsService.buildingsForUpdate.length === 1) {
                this.buildingId.setValue(this.parcelsService.buildingsForUpdate[0].id);
            }

            this.showModal = true;
        }
    }

    hideModal(silently = false) {
        const hide = () => {
            this.parcelsService.isParcelModalShow = false;
            this.parcelsService.parcelInParcelModal = null;
            this.urlService.modalClosed.emit();
        };
        if (this.form.dirty && this.form.touched && !silently) {
            swal(ConstantsService.getSwalConfig('Close without saving?', 'Do you want to delete unsaved data?'), hide);
        } else {
            hide();
        }
    }

    onSubmit() {
        if (this.form.invalid) {
            if (this.buildingId.invalid) toastr.warning('Building should not be blank.');
            if (this.suiteId.invalid) toastr.warning('Unit should not be blank.');
            if (this.residentId.invalid) toastr.warning('Resident should not be blank.');
            if (this.numberPieces.invalid) toastr.warning('Pieces can not be less than 1');
            return;
        }
        const building = this.parcelsService.buildings.find((item) => +item.id === +this.buildingId.value);
        const suite = this.suites.find((item) => +item.value === +this.suiteId.value);
        const resident = this.residents.find((item) => +item.id === +this.residentId.value);

        if (!suite) {
            toastr.warning('Suite not found.');
            return;
        }
        if (!resident) {
            toastr.warning('Resident not found.');
            return;
        }
        const formValue = this.form.value;
        const data: any = {
            building: {
                bdId: formValue.buildingId,
                name: building ? building.name : null,
                address: building ? building.address : null,
                corporationNumberBd: building ? building.corporationNumber : null,
                timezone: building ? (building.timeZone ? building.timeZone.value : null) : null
            },
            suite: {
                bdId: formValue.suiteId > 0 ? formValue.suiteId : null,
                suiteNumber: formValue.suiteNumber,
            },
            resident: {
                bdId: formValue.residentId > 0 ? formValue.residentId : null,
                fullName: formValue.residentFullName,
                mobilePhone: resident.getMobilePhone()
            },
            parcelPostService: formValue.parcelPostServiceId == -1 ? null : formValue.parcelPostServiceId,
            parcelPostServiceName: formValue.parcelPostServiceName,
            numberPieces: formValue.numberPieces,
            barCode: formValue.barCode,
            parcelType: formValue.parcelTypeId,
            inOut: formValue.inOut,
            description: formValue.description,
            createdAt: this.getDateTimeFromForm(),
        };
        if (formValue.emailNotice != undefined) {
            data.emailNotice = +formValue.emailNotice;
        }
        if (this.data && !this.data.id) {
            if (formValue.smsNotice != undefined) data.smsNotice = +formValue.smsNotice;
        }
        if (resident && resident.email) data.resident.email = resident.email;
        if (resident && resident.getPrimaryPhone()) data.resident.phone = resident.getPrimaryPhone();

        if (!resident.parcelWaiverSigned && resident.id != 0) {
            swal(ConstantsService.getSwalConfig(
                'Are you sure?',
                'You are about to receive a package on behalf of a resident who has not signed a waiver, would you like to continue?'
                ),
                () => this.save(data)
            );
        } else {
            this.save(data);
        }
    }

    save(data) {
        this.loading.global = true;
        if (this.data && this.data.id) {
            this.api.patch('domainParcels', `/v1/parcels/${this.data.id}.json`, data)
                .pipe(
                    finalize(() => this.loading.global = false)
                )
                .subscribe(
                    () => this.updateView('Parcel successfully updated'),
                    error => {
                        this.api.errorHandler(error, 'Edit parcel failed');
                    }
                );
        } else {
            this.api.post('domainParcels', '/v1/parcels.json', data)
                .pipe(
                    finalize(() => this.loading.global = false)
                )
                .subscribe(
                    () => this.updateView('Parcel successfully created'),
                    error => {
                        this.api.errorHandler(error, 'Create parcel failed');
                    }
                );
        }
    }

    getDateTimeFromForm(): Date {
        const date = new Date(this.form.value.date);
        const time = this.form.value.time;
        const timeObj = new Date(`01/01/1970 ${time}`);
        const hours = timeObj.getHours();
        const minutes = timeObj.getMinutes();
        date.setHours(hours);
        date.setMinutes(minutes);
        return date;
    }

    updateView(msg: string): void {
        toastr.success(msg);
        this.parcelsService.getData();
        this.hideModal(true);
    }

    changeBuilding(buildingId: string) {
        if (this.showModal) {
            this.suiteId.setValue(null, {emitEvent: false});
            this.parcelTypeId.reset();
        }
        this.getSuites(+buildingId).subscribe();
    }

    changeUnit(suiteId) {
        this.residentId.reset();
        this.getResidents(+suiteId)
            .pipe(
                tap(() => {
                    const selectedSuite = this.suites.find(v => v.value === suiteId);
                    if (suiteId == 0) {
                        this.form.patchValue({
                            residentId: 0,
                            suiteNumber: '',
                            residentFullName: ''
                        });
                    } else if (selectedSuite) {
                        this.form.patchValue({
                            suiteNumber: selectedSuite.optionText
                        });
                    }
                })
            )
            .subscribe();
    }

    changeResident(residentId: number) {
        this.selectedResident = this.residents.find(resident => resident.id === residentId);
        if (!residentId) {
            this.form.controls.residentFullName.setValue('');
        } else if (this.selectedResident) {
            this.selectedSuiteUser = this.selectedResident.getSuiteUserBySuite(this.suiteId.value);
            this.form.patchValue({residentFullName: this.selectedResident.fullName});
        }
    }

    getSuites(buildingId: number, suiteId: number = null) {
        let source = this.buildingApiService.getSuitesSelect({
            'per-page': 1000,
            'order-by': 'suiteNumber|asc',
            building_id: buildingId
        });
        if (!buildingId) source = of({
            entities: [],
            total: undefined
        });
        this.loading.suites = true;
        return source
            .pipe(
                map(
                    suitesResponse => {
                        const suites = suitesResponse.entities.map((suite) => ({value: suite.id, optionText: suite.suiteNumber}));
                        suites.push({value: '0', optionText: 'Other'});
                        return suites;
                    }
                ),
                tap(suites => this.suites = suites),
                tap(
                    () => this.loading.suites = false,
                    () => this.loading.suites = false
                ),
                flatMap(() => this.getResidents(suiteId)),
                flatMap(
                    () => {
                        if (buildingId) {
                            const building = this.parcelsService.buildings.find(b => b.id == this.buildingId.value);
                            this.selectedBuildingCorporation = building.corporationNumber;
                            return this.getCorporationSettings(building);
                        } else {
                            return of(null);
                        }
                    }
                ),
                tap(() => this.showModal = true),
                untilDestroyed(this)
            );
    }

    getResidents(suiteId: number = null) {
        let source = this.buildingApiService.getResidentsForSelect({
            suite_id: suiteId,
            expand: 'suiteUser',
            'per-page': 1000
        });
        if (suiteId === null || this.suiteId.value == 0) {
            source = of([new ResidentForSelect(0, 'Other')]);
        }
        this.loading.residents = true;
        return source
            .pipe(
                tap(
                    residents => this.residents = residents,
                    error => this.api.errorHandler(error, 'Failed get Residents')
                ),
                tap(
                    () => this.loading.residents = false,
                    () => this.loading.residents = false
                ),
                untilDestroyed(this)
            );
    }

    residentsToSearchSelect(arr: Array<any>) {
        return arr.map(resident => ({
            ...resident,
            value: resident.id,
            optionText: resident.fullName
        }));
    }

    /**
     * Returns email for selected resident
     */
    getResidentEmail(): string {
        if (this.selectedResident) {
            return this.selectedResident.email || '';
        }

        return '';
    }

    /**
     * Returns phones list for selected resident
     */
    getResidentPhones(): Array<UserPhoneType> {
        if (this.selectedResident) {
            return this.selectedResident.phones;
        }
        return [];
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
        this.hideModal();
    }

    getCorporationSettings(building: IBuilding) {
        return this.buildingApiService.getCorporationSettings(building.corporationNumber as number, {expand: 'settings'})
            .pipe(
                tap(
                    ({useParcelsWaiver}) => this.useParcelsWaiver = useParcelsWaiver,
                    error => this.api.errorHandler(error, 'Failed get Services')
                ),
                flatMap(() => {
                    if (this.accountPermissionsService.canRead('sms', building.id)) {
                        return this.getSmsSettingsForCorporation(building.corporationNumber);
                    }
                    return of(null);
                })
            );
    }

    getSmsSettingsForCorporation(corporationNumberId) {
        return this.smsNotificationApiService.corporationNumberSettings(corporationNumberId)
            .pipe(
                tap(
                    res => this.smsSettings = res,
                    error => {
                        if (error.statusText == 'Not Found') {
                            this.smsSettings = new SettingsSms();
                        }
                    }
                )
            );
    }

    smsIsEnabled() {
        return this.smsSettings && this.smsSettings.isEnable &&
            this.smsSettings.twilioSettings.sid && this.smsSettings.twilioSettings.friendlyName;
    }

    ngOnDestroy(): void {
    }

    changeParcelPostService(v: number) {
        if (v != -1) {
            const selectedParcelPostService = this.parcelsService.postServices.find(service => service.id === v);
            if (selectedParcelPostService) {
                this.form.patchValue({parcelPostServiceName: selectedParcelPostService.name});
            }
        } else {
            this.form.patchValue({parcelPostServiceName: ''});
        }
    }

    canEditDirectory() {
        return this.accountService.canUpdate('bldn', this.buildingId.value);
    }

    private listenToValueChanges() {
        this.buildingId.valueChanges
            .pipe(
                tap(v => this.changeBuilding(v)),
                untilDestroyed(this)
            )
            .subscribe();

        this.suiteId.valueChanges
            .pipe(
                tap(v => this.changeUnit(v)),
                untilDestroyed(this)
            )
            .subscribe();
        this.residentId.valueChanges
            .pipe(
                tap(v => this.changeResident(v)),
                untilDestroyed(this)
            )
            .subscribe();

        this.parcelPostServiceId.valueChanges
            .pipe(
                tap(v => this.changeParcelPostService(v)),
                untilDestroyed(this)
            )
            .subscribe();
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/parcels/parcel-modal/parcel-modal.component.ts